import { useEffect, useLayoutEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import Lenis from 'lenis';
import About from './About';
import Blog from './Blog';
import BlogDetail from './BlogDetail';
import CaseStudy from './CaseStudy';
import CaseStudyDetail from './CaseStudyDetail';
import Contact from './Contact';
import DigitalMarketing from './DigitalMarketing';
import Home from './Home';
import NotFound from './NotFound';
import BackToTop from './components/BackToTop';
import Footer from './components/Footer';
import Header from './components/Header';
import Faqs from './Faqs';
import Services from './Services';
import Specialities from './Specialities';
import './App.css';
import PrivacyPolicy from './PrivacyPolicy';
import TermsConditions from './TermsConditions';
import MedicalBilling from './Services/MedicalBilling';
import CredentialingEnrollment from './Services/CredentialingEnrollment';
import DenialManagement from './Services/DenialManagement';
import AccountsReceivables from './Services/AccountsReceivables';
import PriorAuthorization from './Services/PriorAuthorization';
import VerificationBenefits from './Services/VerificationBenefits';
import VirtualMedicalScribe from './Services/VirtualMedicalScribe';
import VirtualAssistant from './Services/VirtualAssistant';
import appRoutes from './data/routes.json';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import AdminApp from './appcomponents/AdminApp';
import ProtectedRoute from './appcomponents/ProtectedRoute';



gsap.registerPlugin(ScrollToPlugin);

let smoothScroller = null;

const routeComponents = {
  home: <Home />,
  about: <About />,
  blog: <Blog />,
  blogDetail: <BlogDetail />,
  caseStudy: <CaseStudy />,
  caseStudyDetail: <CaseStudyDetail />,
  contact: <Contact />,
  services: <Services />,
  medicalBilling: <MedicalBilling />,
  denialManagement: <DenialManagement />,
  credentialingEnrollment: <CredentialingEnrollment />,
  accountsReceivables: <AccountsReceivables />,
  priorAuthorization: <PriorAuthorization />,
  verificationBenefits: <VerificationBenefits />,
  virtualMedicalScribe: <VirtualMedicalScribe />,
  virtualAssistant: <VirtualAssistant />,
  specialities: <Specialities />,
  digitalMarketing: <DigitalMarketing />,
  faqs: <Faqs />,
  privacyPolicy: <PrivacyPolicy />,
  termsConditions: <TermsConditions />,
  notFound: <NotFound />,
};

function SmoothScrollManager() {
  useEffect(() => {
    smoothScroller = new Lenis({
      duration: 2.1,
      smoothWheel: true,
      smoothTouch: false,
      wheelMultiplier: 0.64,
      touchMultiplier: 0.88,
    });
    window.__lenis = smoothScroller;

    const update = (time) => {
      smoothScroller?.raf(time * 1000);
    };

    gsap.ticker.add(update);
    gsap.ticker.lagSmoothing(0);

    return () => {
      gsap.ticker.remove(update);
      smoothScroller?.destroy();
      smoothScroller = null;
      delete window.__lenis;
    };
  }, []);

  return null;
}

function ScrollManager() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useLayoutEffect(() => {
    const targetId = hash.replace('#', '');
    let retryTimer;
    let isCancelled = false;

    const animateTo = () => {
      if (isCancelled) {
        return;
      }

      gsap.killTweensOf(window);

      if (targetId) {
        const target = document.getElementById(targetId);
        if (target) {
          if (smoothScroller) {
            smoothScroller.scrollTo(target, {
              offset: -110,
              duration: 1.2,
              lock: true,
            });
          } else {
            gsap.to(window, {
              duration: 0.9,
              ease: 'power2.out',
              scrollTo: { y: target, offsetY: 110, autoKill: false },
            });
          }
          return;
        }

        retryTimer = window.setTimeout(animateTo, 120);
        return;
      }

      if (smoothScroller) {
        smoothScroller.scrollTo(0, {
          duration: 1,
          lock: true,
        });
      } else {
        gsap.to(window, {
          duration: 0.7,
          ease: 'power2.out',
          scrollTo: { y: 0, autoKill: false },
        });
      }
    };

    const frame = window.requestAnimationFrame(animateTo);
    return () => {
      isCancelled = true;
      window.cancelAnimationFrame(frame);
      window.clearTimeout(retryTimer);
      gsap.killTweensOf(window);
    };
  }, [pathname, hash]);

  return null;
}

function TrailingSlashRedirect() {
  const location = useLocation();
  const { pathname, search, hash } = location;

  if (
    pathname !== '/' &&
    !pathname.endsWith('/') &&
    !pathname.includes('.') &&
    pathname !== '*'
  ) {
    return <Navigate replace to={`${pathname}/${search}${hash}`} />;
  }

  return null;
}

function MarketingApp() {
  return (
    <div className="app-shell">
      <TrailingSlashRedirect />
      <SmoothScrollManager />
      <ScrollManager />
      <Header />
      <main>
        <Routes>
          {appRoutes.map((route) => (
            <Route key={route.path} path={route.path} element={routeComponents[route.page]} />
          ))}
        </Routes>
      </main>
      <BackToTop />
      <Footer />
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/auth/admin/login/" element={<Login mode="admin" />} />
      <Route path="/auth/admin/login" element={<Login mode="admin" />} />
      <Route path="/auth/user/login/" element={<Login mode="user" />} />
      <Route path="/auth/user/login" element={<Login mode="user" />} />
      <Route path="/auth/reset-password/" element={<ResetPassword />} />
      <Route path="/auth/reset-password" element={<ResetPassword />} />
      <Route path="/admin/login" element={<Navigate to="/auth/admin/login/" replace />} />
      <Route
        path="/user/*"
        element={
          <ProtectedRoute>
            <AdminApp basePath="user" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute>
            <AdminApp />
          </ProtectedRoute>
        }
      />
      <Route path="/*" element={<MarketingApp />} />
    </Routes>
  );
}
